import { createContext, useContext } from 'react';

// Toast context + hook, split out of Toast.tsx so the component file has a
// single component export (keeps react-refresh/only-export-components happy).
// Use InlineError for in-place section errors; use a toast for transient
// "it worked" / "it failed" feedback on an action that navigates or closes a modal.

export type ToastKind = 'success' | 'error';
export type ToastContextValue = {
  /**
   * Show a toast and return its id. Most callers ignore the id — the toast expires on its
   * own. Keep it when the toast describes a CONDITION rather than an event, so the code
   * that observes the condition clearing can retract the message instead of leaving a
   * stale one on screen for the rest of the TTL (see `useLiveStatus`, A1EE-364).
   */
  showToast: (message: string, kind?: ToastKind) => number;
  /** Retract a toast early. A no-op if it has already been dismissed or expired. */
  dismissToast: (id: number) => void;
};

export const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
